import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Trash2, Plus, Minus, ShoppingBag, Store, Truck } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useCart } from '../contexts/CartContext';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SHIPPING_COST = 7.26; // 6€ + 21% IVA
const IVA_RATE = 0.21;

const Cart = () => {
  const { cart, removeFromCart, updateQuantity, getCartTotal, clearCart } = useCart();
  const [loading, setLoading] = useState(false);
  const [shippingMethod, setShippingMethod] = useState('pickup');
  const [customerInfo, setCustomerInfo] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    postal_code: ''
  });
  const navigate = useNavigate();

  const handleCheckout = async (e) => {
    e.preventDefault();
    
    if (cart.length === 0) {
      toast.error('La cistella està buida');
      return;
    }

    // Validate customer info
    if (!customerInfo.name || !customerInfo.email || !customerInfo.phone) {
      toast.error('Si us plau, omple tots els camps obligatoris');
      return;
    }

    if (shippingMethod === 'delivery' && (!customerInfo.address || !customerInfo.city || !customerInfo.postal_code)) {
      toast.error('Si us plau, omple l\'adreça d\'enviament');
      return;
    }

    setLoading(true);
    try {
      const subtotal = getCartTotal();
      const shippingCost = shippingMethod === 'delivery' ? SHIPPING_COST : 0;
      const total = subtotal + shippingCost;

      // Create order
      const orderData = {
        items: cart.map(item => ({
          product_id: item.product.id,
          quantity: item.quantity
        })),
        subtotal,
        shipping_cost: shippingCost,
        total,
        shipping_method: shippingMethod,
        customer_info: customerInfo
      };

      const orderResponse = await axios.post(`${API}/orders`, orderData);
      const orderId = orderResponse.data.id;

      // Create checkout session
      const checkoutData = {
        order_id: orderId,
        origin_url: window.location.origin
      };

      const checkoutResponse = await axios.post(`${API}/checkout/session`, checkoutData);
      
      // Redirect to Stripe
      window.location.href = checkoutResponse.data.url;
    } catch (error) {
      console.error('Error al processar el pagament:', error);
      toast.error('Error al processar el pagament. Si us plau, torna-ho a intentar.');
      setLoading(false);
    }
  };

  if (cart.length === 0) {
    return (
      <div data-testid="empty-cart" className="min-h-screen pt-32 pb-24 px-6 md:px-12 flex items-center justify-center">
        <div className="text-center">
          <ShoppingBag className="h-24 w-24 text-muted-foreground mx-auto mb-6" />
          <h2 className="font-heading text-3xl font-medium text-foreground mb-4">
            La teva cistella està buida
          </h2>
          <p className="text-muted-foreground mb-8">
            Descobreix els nostres productes i afegeix-los a la cistella
          </p>
          <Link to="/catalog">
            <Button data-testid="continue-shopping" size="lg" className="rounded-full">
              Continuar comprant
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="cart-page" className="min-h-screen pt-32 pb-24 px-6 md:px-12">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h1 className="font-heading text-4xl md:text-6xl font-medium tracking-tight text-foreground mb-12">
            La Meva Cistella
          </h1>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Cart Items */}
            <div className="lg:col-span-2 space-y-6">
              {cart.map((item) => {
                const finalPrice = item.product.discount > 0
                  ? item.product.price * (1 - item.product.discount / 100)
                  : item.product.price;

                return (
                  <div 
                    key={item.product.id} 
                    data-testid={`cart-item-${item.product.id}`}
                    className="bg-card border border-border rounded-2xl p-6 flex gap-6"
                  >
                    <div className="w-24 h-24 rounded-xl overflow-hidden bg-muted flex-shrink-0">
                      <img
                        src={item.product.image_url}
                        alt={item.product.name}
                        className="w-full h-full object-cover"
                      />
                    </div>

                    <div className="flex-1">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <h3 className="font-body text-lg font-medium text-foreground">
                            {item.product.name}
                          </h3>
                          <p className="text-sm text-muted-foreground">
                            {finalPrice.toFixed(2).replace('.', ',')} €
                          </p>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => removeFromCart(item.product.id)}
                          data-testid={`remove-item-${item.product.id}`}
                          className="text-destructive hover:text-destructive"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>

                      <div className="flex items-center gap-3">
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => updateQuantity(item.product.id, item.quantity - 1)}
                          data-testid={`decrease-${item.product.id}`}
                          className="h-8 w-8"
                        >
                          <Minus className="h-3 w-3" />
                        </Button>
                        <span data-testid={`quantity-${item.product.id}`} className="text-sm font-medium min-w-[2rem] text-center">
                          {item.quantity}
                        </span>
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => updateQuantity(item.product.id, item.quantity + 1)}
                          data-testid={`increase-${item.product.id}`}
                          className="h-8 w-8"
                        >
                          <Plus className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Checkout Form & Summary */}
            <div className="lg:col-span-1">
              <form onSubmit={handleCheckout} className="space-y-6">
                {/* Customer Info */}
                <div className="bg-card rounded-2xl p-6 border border-border">
                  <h3 className="font-heading text-xl font-medium text-foreground mb-4">
                    Dades de contacte
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="name">Nom complet *</Label>
                      <Input
                        id="name"
                        value={customerInfo.name}
                        onChange={(e) => setCustomerInfo({...customerInfo, name: e.target.value})}
                        className="mt-1"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="email">Email *</Label>
                      <Input
                        id="email"
                        type="email"
                        value={customerInfo.email}
                        onChange={(e) => setCustomerInfo({...customerInfo, email: e.target.value})}
                        className="mt-1"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="phone">Telèfon *</Label>
                      <Input
                        id="phone"
                        type="tel"
                        value={customerInfo.phone}
                        onChange={(e) => setCustomerInfo({...customerInfo, phone: e.target.value})}
                        className="mt-1"
                        required
                      />
                    </div>
                  </div>
                </div>

                {/* Shipping Method */}
                <div className="bg-card rounded-2xl p-6 border border-border">
                  <h3 className="font-heading text-xl font-medium text-foreground mb-4">
                    Mètode d'entrega
                  </h3>
                  <div className="space-y-3">
                    <label className={`flex items-center gap-3 p-4 border-2 rounded-xl cursor-pointer transition-colors ${shippingMethod === 'pickup' ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'}`}>
                      <input
                        type="radio"
                        name="shipping"
                        value="pickup"
                        checked={shippingMethod === 'pickup'}
                        onChange={(e) => setShippingMethod(e.target.value)}
                        className="w-4 h-4"
                      />
                      <Store className="h-5 w-5 text-primary" />
                      <div className="flex-1">
                        <p className="font-medium text-foreground">Recollida a botiga</p>
                        <p className="text-sm text-muted-foreground">Gratuït</p>
                      </div>
                      <span className="font-semibold text-green-600">0,00 €</span>
                    </label>

                    <label className={`flex items-center gap-3 p-4 border-2 rounded-xl cursor-pointer transition-colors ${shippingMethod === 'delivery' ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'}`}>
                      <input
                        type="radio"
                        name="shipping"
                        value="delivery"
                        checked={shippingMethod === 'delivery'}
                        onChange={(e) => setShippingMethod(e.target.value)}
                        className="w-4 h-4"
                      />
                      <Truck className="h-5 w-5 text-primary" />
                      <div className="flex-1">
                        <p className="font-medium text-foreground">Enviament a domicili</p>
                        <p className="text-sm text-muted-foreground">6€ + IVA</p>
                      </div>
                      <span className="font-semibold text-foreground">7,26 €</span>
                    </label>
                  </div>
                </div>

                {/* Delivery Address */}
                {shippingMethod === 'delivery' && (
                  <div className="bg-card rounded-2xl p-6 border border-border">
                    <h3 className="font-heading text-xl font-medium text-foreground mb-4">
                      Adreça d'enviament
                    </h3>
                    <div className="space-y-4">
                      <div>
                        <Label htmlFor="address">Adreça *</Label>
                        <Input
                          id="address"
                          value={customerInfo.address}
                          onChange={(e) => setCustomerInfo({...customerInfo, address: e.target.value})}
                          className="mt-1"
                          required={shippingMethod === 'delivery'}
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="city">Ciutat *</Label>
                          <Input
                            id="city"
                            value={customerInfo.city}
                            onChange={(e) => setCustomerInfo({...customerInfo, city: e.target.value})}
                            className="mt-1"
                            required={shippingMethod === 'delivery'}
                          />
                        </div>
                        <div>
                          <Label htmlFor="postal_code">Codi postal *</Label>
                          <Input
                            id="postal_code"
                            value={customerInfo.postal_code}
                            onChange={(e) => setCustomerInfo({...customerInfo, postal_code: e.target.value})}
                            className="mt-1"
                            required={shippingMethod === 'delivery'}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Order Summary */}
                <div className="bg-muted rounded-2xl p-8 sticky top-32">
                  <h2 className="font-heading text-2xl font-medium text-foreground mb-6">
                    Resum de la comanda
                  </h2>

                  <div className="space-y-4 mb-6">
                    <div className="flex justify-between text-base">
                      <span className="text-muted-foreground">Subtotal</span>
                      <span className="font-medium">{getCartTotal().toFixed(2).replace('.', ',')} €</span>
                    </div>
                    <div className="flex justify-between text-base">
                      <span className="text-muted-foreground">Enviament</span>
                      <span className="font-medium">
                        {shippingMethod === 'delivery' ? `${SHIPPING_COST.toFixed(2).replace('.', ',')} €` : 'Gratuït'}
                      </span>
                    </div>
                    <div className="border-t border-border pt-4">
                      <div className="flex justify-between text-lg font-semibold">
                        <span>Total</span>
                        <span data-testid="cart-total">
                          {(getCartTotal() + (shippingMethod === 'delivery' ? SHIPPING_COST : 0)).toFixed(2).replace('.', ',')} €
                        </span>
                      </div>
                    </div>
                  </div>

                  <Button
                    type="submit"
                    disabled={loading}
                    data-testid="checkout-button"
                    className="w-full rounded-xl h-14 text-base font-medium"
                    size="lg"
                  >
                    {loading ? 'Processant...' : 'Procedir al pagament'}
                  </Button>

                  <Link to="/catalog">
                    <Button 
                      variant="ghost" 
                      className="w-full mt-4"
                      data-testid="continue-shopping-link"
                    >
                      Continuar comprant
                    </Button>
                  </Link>
                </div>
              </form>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Cart;